import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -12 } };

function buildItems(def) {
  const items = [];
  def.components.forEach((c) => {
    items.push({ type: "intro", component: c.name, isLegacy: c.is_legacy, intro: c.intro });
    c.questions.forEach((q) => items.push({ type: "question", component: c.name, isLegacy: c.is_legacy, q }));
  });
  return items;
}

export default function Assessment() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resumeMode = params.get("resume") === "1";

  const [stage, setStage] = useState(resumeMode ? "resume" : "lead");
  const [def, setDef] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", sector: "", designation: "" });
  const [resumeEmail, setResumeEmail] = useState("");

  const items = useMemo(() => (def ? buildItems(def) : []), [def]);
  const questionItems = useMemo(() => items.filter((i) => i.type === "question"), [items]);
  const totalQuestions = questionItems.length;
  const answeredCount = Object.keys(answers).length;
  const percent = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const loadDef = useCallback(async () => {
    const res = await api.get("/assessment/active");
    setDef(res.data);
    return res.data;
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startAssessment = async () => {
    const missing = Object.entries(form).filter(([, v]) => !v.trim());
    if (missing.length) { toast.error("Please complete all fields to begin."); return; }
    setStarting(true);
    try {
      const d = await loadDef();
      const res = await api.post("/assessment/start", form);
      setAssessmentId(res.data.assessment_id);
      setAnswers(res.data.answers || {});
      const its = buildItems(d);
      const savedStep = res.data.current_component_index || 0;
      setStep(Math.min(savedStep, its.length - 1));
      setStage("assessment");
      if (res.data.resumed) toast.success("Welcome back — your progress has been restored.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start the assessment.");
    } finally {
      setStarting(false);
    }
  };

  const doResume = async () => {
    if (!resumeEmail.trim()) { toast.error("Enter your email to resume."); return; }
    setStarting(true);
    try {
      const d = await loadDef();
      const res = await api.post("/assessment/resume", { email: resumeEmail });
      setAssessmentId(res.data.assessment_id);
      setAnswers(res.data.answers || {});
      setForm({ ...res.data.respondent });
      const its = buildItems(d);
      setStep(Math.min(res.data.current_component_index || 0, its.length - 1));
      setStage("assessment");
      toast.success("Your saved assessment has been restored.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No saved assessment found for that email.");
    } finally {
      setStarting(false);
    }
  };

  const persist = useCallback(async (nextAnswers, nextStep) => {
    if (!assessmentId) return;
    try {
      await api.post(`/assessment/${assessmentId}/save`, {
        answers: nextAnswers, current_component_index: nextStep,
      });
    } catch (e) {}
  }, [assessmentId]);

  const selectOption = (qid, optIdx) => {
    const next = { ...answers, [qid]: optIdx };
    setAnswers(next);
    persist(next, step);
  };

  const current = items[step];

  const goNext = () => {
    if (current?.type === "question" && answers[current.q.qid] === undefined) {
      toast.error("Please select an option to continue."); return;
    }
    const ns = Math.min(step + 1, items.length - 1);
    setStep(ns); persist(answers, ns);
  };
  const goBack = () => { const ns = Math.max(step - 1, 0); setStep(ns); persist(answers, ns); };

  const saveForLater = async () => {
    await persist(answers, step);
    toast.success("Progress saved. Resume anytime using your email.");
  };

  const submit = async () => {
    if (answeredCount < totalQuestions) {
      toast.error(`Please answer all questions (${answeredCount}/${totalQuestions}).`); return;
    }
    setSubmitting(true);
    try {
      await api.post(`/assessment/${assessmentId}/submit`, { answers, current_component_index: step });
      navigate(`/result/${assessmentId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
      setSubmitting(false);
    }
  };

  // ---------- LEAD FORM ----------
  if (stage === "lead") {
    return (
      <Shell>
        <motion.div {...fade} transition={{ duration: 0.5 }} className="max-w-2xl mx-auto">
          <div className="overline mb-3">Confidential Executive Evaluation</div>
          <h1 className="font-serif text-4xl md:text-5xl text-oxford tracking-tight">Let's begin.</h1>
          <p className="mt-4 text-[var(--text2)] leading-relaxed">
            A few details about you and your organization. Your responses remain private and are used
            only to prepare your scorecard.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-6" data-testid="lead-form">
            <Field label="Full Name" k="name" form={form} onChange={setField} testid="lead-name" />
            <Field label="Email Address" k="email" type="email" form={form} onChange={setField} testid="lead-email" />
            <Field label="Phone Number" k="phone" form={form} onChange={setField} testid="lead-phone" />
            <Field label="Company Name" k="company" form={form} onChange={setField} testid="lead-company" />
            <Field label="Sector / Industry" k="sector" form={form} onChange={setField} testid="lead-sector" />
            <Field label="Designation / Role" k="designation" form={form} onChange={setField} testid="lead-designation" />
          </div>
          <div className="mt-10 flex items-center gap-4">
            <Button data-testid="lead-submit-btn" onClick={startAssessment} disabled={starting}
              className="btn-primary text-white h-12 px-8">
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
            <button onClick={() => setStage("resume")} data-testid="switch-resume-btn"
              className="text-sm text-[var(--text2)] underline underline-offset-4 hover:text-oxford">
              Resume instead
            </button>
          </div>
        </motion.div>
      </Shell>
    );
  }

  // ---------- RESUME ----------
  if (stage === "resume") {
    return (
      <Shell>
        <motion.div {...fade} transition={{ duration: 0.5 }} className="max-w-xl mx-auto">
          <div className="overline mb-3">Resume Assessment</div>
          <h1 className="font-serif text-4xl text-oxford tracking-tight">Welcome back.</h1>
          <p className="mt-4 text-[var(--text2)]">Enter the email you used to continue where you left off.</p>
          <div className="mt-8">
            <Label className="overline">Email Address</Label>
            <Input data-testid="resume-email-input" type="email" value={resumeEmail}
              onChange={(e) => setResumeEmail(e.target.value)}
              className="mt-2 h-12 bg-white border-[var(--brdr)]" placeholder="you@company.com" />
          </div>
          <div className="mt-8 flex items-center gap-4">
            <Button data-testid="resume-submit-btn" onClick={doResume} disabled={starting}
              className="btn-primary text-white h-12 px-8">
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume"}
            </Button>
            <button onClick={() => setStage("lead")} className="text-sm text-[var(--text2)] underline underline-offset-4">
              Start new instead
            </button>
          </div>
        </motion.div>
      </Shell>
    );
  }

  // ---------- ASSESSMENT ----------
  const questionNumber = current?.type === "question"
    ? questionItems.findIndex((qi) => qi.q.qid === current.q.qid) + 1
    : Math.min(questionItems.filter((qi) => items.indexOf(qi) < step).length + 1, totalQuestions);
  const isLast = step === items.length - 1;

  return (
    <div className="min-h-screen bg-alabaster flex flex-col">
      {/* Top progress */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[var(--brdr)]">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between text-xs text-[var(--text2)]">
            <Link to="/"><img src={LOGO} alt="Aikyam" className="h-8 w-auto" /></Link>
            <span data-testid="progress-percent" className="font-mono">{percent}% complete · {answeredCount}/{totalQuestions}</span>
          </div>
          <div className="mt-3 h-1 bg-[var(--brdr)] rounded-full overflow-hidden">
            <motion.div className="h-full bg-oxford" animate={{ width: `${percent}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-14 md:py-20">
        <AnimatePresence mode="wait">
          {current?.type === "intro" ? (
            <motion.div key={`intro-${step}`} {...fade} transition={{ duration: 0.4 }}
              className="text-center py-10" data-testid="section-intro">
              <div className="overline text-gold mb-4">{current.isLegacy ? "Legacy Dimension" : "Component"}</div>
              <h2 className="font-serif text-3xl md:text-4xl text-oxford tracking-tight">{current.component}</h2>
              <p className="mt-5 text-[var(--text2)] max-w-xl mx-auto leading-relaxed">
                {current.intro || `The following questions explore your organization's ${current.component.toLowerCase()}. Choose the statement that best reflects your business today.`}
              </p>
              <Button data-testid="intro-continue-btn" onClick={goNext} className="btn-primary text-white h-12 px-8 mt-10">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          ) : current ? (
            <motion.div key={current.q.qid} {...fade} transition={{ duration: 0.4 }} data-testid="question-card">
              <div className="flex items-center justify-between mb-6">
                <div className="overline">{current.component}{current.q.dimension ? ` · ${current.q.dimension}` : ""}</div>
                <div className="font-mono text-xs text-[var(--text2)]" data-testid="question-counter">
                  Question {questionNumber} of {totalQuestions}
                </div>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-oxford leading-snug tracking-tight">
                {current.q.text}
              </h2>
              <div className="mt-8 space-y-3">
                {current.q.options.map((opt, i) => {
                  const selected = answers[current.q.qid] === i;
                  return (
                    <motion.button key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                      data-testid={`option-${current.q.qid}-${i}`}
                      onClick={() => selectOption(current.q.qid, i)}
                      className={`option-card w-full text-left p-5 flex items-start gap-4 ${selected ? "selected" : ""}`}>
                      <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border flex items-center justify-center
                        ${selected ? "bg-oxford border-oxford" : "border-[var(--brdr)]"}`}>
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="text-sm md:text-base text-oxford leading-relaxed">{opt}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Bottom nav */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur-md border-t border-[var(--brdr)]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button data-testid="back-btn" variant="ghost" onClick={goBack} disabled={step === 0}
            className="text-[var(--text2)]">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <button data-testid="save-later-btn" onClick={saveForLater}
            className="text-xs uppercase tracking-[0.15em] text-[var(--text2)] hover:text-oxford flex items-center gap-2">
            <Save className="h-3.5 w-3.5" /> Save &amp; resume later
          </button>
          {isLast ? (
            <Button data-testid="submit-btn" onClick={submit} disabled={submitting || answeredCount < totalQuestions}
              className="btn-primary text-white h-11 px-7 disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit <Check className="ml-2 h-4 w-4" /></>}
            </Button>
          ) : (
            <Button data-testid="next-btn" onClick={goNext} className="btn-primary text-white h-11 px-7">
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-alabaster">
    <header className="border-b border-[var(--brdr)] bg-white">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
        <Link to="/"><img src={LOGO} alt="Aikyam" className="h-9 w-auto" /></Link>
      </div>
    </header>
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">{children}</div>
  </div>
);

const Field = ({ label, k, type = "text", form, onChange, testid }) => (
  <div>
    <Label className="overline">{label}</Label>
    <Input data-testid={testid} type={type} value={form[k]} onChange={(e) => onChange(k, e.target.value)}
      className="mt-2 h-12 bg-white border-[var(--brdr)] focus-visible:ring-oxford" />
  </div>
);
